/**
 * „Song analysieren“: Datei waehlen → Analyse auf dem Geraet → Ergebnis
 * pruefen und korrigieren → als Song speichern.
 *
 * Datenschutz (docs/SONG-ANALYSE.md): Die Datei wird nur gelesen, im
 * Arbeitsspeicher dekodiert und an einen eigenen Worker uebergeben. Nichts
 * davon landet in IndexedDB, Firestore, Logs oder beim Server. Nach
 * Abschluss oder Abbruch wird der Worker beendet, das Audio ist weg.
 * Gespeichert wird nur, was der Nutzer mit „Als Song speichern“ bestaetigt.
 */
import { h } from '../../ui/dom.ts';
import { icon } from '../../ui/icons.ts';
import { askText } from '../../ui/ask.ts';
import { toast } from '../../ui/toast.ts';
import { emptySong, saveSong } from '../../db/repo.ts';
import type { Song } from '../../types.ts';
import { AudioFileError, decodeForAnalysis, titleFromFileName } from './audio-file.ts';
import { CHECK, CONFIDENT, cleanChord, cleanLabel, keyText, toSheetText, validate, type LeadSheetAnalysis } from './schema.ts';
import type { WorkerIn, WorkerOut } from './worker.ts';

const STEPS: { id: string; text: string }[] = [
  { id: 'read', text: 'Audio lesen' },
  { id: 'notes', text: 'Töne erkennen' },
  { id: 'chords', text: 'Rhythmus und Akkorde' },
  { id: 'structure', text: 'Songstruktur' },
  { id: 'sheet', text: 'Lead Sheet' },
];

const pct = (x: number) => `${Math.round(x * 100)} %`;
const level = (c: number) => (c >= CONFIDENT ? 'ok' : c >= CHECK ? 'soft' : 'check');

export function openAnalysisDialog(projectId: string, onSaved: (song: Song) => void): void {
  let worker: Worker | null = null;
  let closed = false;

  const body = h('div', { class: 'modal__body' });
  const headRight = h('div', { class: 'btn-row' });

  const stop = () => {
    worker?.terminate();
    worker = null;
  };
  const close = () => {
    closed = true;
    stop();
    document.removeEventListener('keydown', onKey);
    modal.remove();
  };
  const onKey = (ev: KeyboardEvent) => ev.key === 'Escape' && close();

  /* ------------------------------------------------------ 1. Datei */

  function paintPick(error?: string): void {
    headRight.replaceChildren(h('button', { class: 'btn btn--small', text: 'Abbrechen', attrs: { type: 'button' }, on: { click: close } }));
    const input = h('input', { attrs: { type: 'file', accept: 'audio/*,.mp3,.wav,.flac,.m4a,.aac,.ogg,.aif,.aiff', hidden: '' } });
    input.addEventListener('change', () => {
      const f = input.files?.[0];
      if (f) void start(f);
    });
    const drop = h(
      'button',
      { class: 'analysis__drop', attrs: { type: 'button' }, on: { click: () => input.click() } },
      icon('upload'),
      h('strong', { text: 'Audiodatei wählen' }),
      h('span', { text: 'oder hierher ziehen · MP3, WAV, FLAC, AAC, M4A, OGG · bis 50 MB, 15 Minuten' }),
    );
    drop.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      drop.classList.add('analysis__drop--over');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('analysis__drop--over'));
    drop.addEventListener('drop', (ev) => {
      ev.preventDefault();
      drop.classList.remove('analysis__drop--over');
      const f = ev.dataTransfer?.files?.[0];
      if (f) void start(f);
    });
    body.replaceChildren(
      drop,
      input,
      ...(error ? [h('p', { class: 'analysis__error', attrs: { role: 'alert' }, text: error })] : []),
      h('p', { class: 'analysis__privacy' }, icon('lock'), h('span', { text: 'Dein Audio bleibt auf deinem Gerät und wird nur für diese Analyse verwendet. Es wird weder gespeichert noch hochgeladen.' })),
    );
  }

  /* ---------------------------------------------------- 2. Analyse */

  async function start(file: File): Promise<void> {
    const title = titleFromFileName(file.name);
    const rows = STEPS.map((s) => h('li', { class: 'analysis__step', data: { state: 'wait' } }, h('span', { class: 'analysis__dot' }), h('span', { text: s.text }), h('span', { class: 'analysis__pct' })));
    const mark = (id: string, state: 'run' | 'done', share?: number) => {
      const i = STEPS.findIndex((s) => s.id === id);
      rows.forEach((r, k) => {
        if (k < i) r.dataset.state = 'done';
      });
      const row = rows[i];
      if (!row) return;
      row.dataset.state = state;
      const p = row.querySelector('.analysis__pct') as HTMLElement;
      p.textContent = share !== undefined && state === 'run' ? pct(share) : '';
    };
    headRight.replaceChildren(h('button', { class: 'btn btn--small', text: 'Abbrechen', attrs: { type: 'button' }, on: { click: close } }));
    body.replaceChildren(h('ol', { class: 'analysis__steps', attrs: { 'aria-live': 'polite' } }, ...rows), h('p', { class: 'hint', text: 'Läuft auf deinem Gerät. Je nach Gerät dauert das ein paar Sekunden bis eine Minute.' }));
    mark('read', 'run');
    let pcm: Float32Array;
    try {
      pcm = await decodeForAnalysis(file);
    } catch (err) {
      if (!closed) paintPick(err instanceof AudioFileError ? err.message : 'Die Datei lässt sich nicht lesen.');
      return;
    }
    if (closed) return;
    mark('notes', 'run', 0);
    worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'song-analyse' });
    worker.onmessage = (ev: MessageEvent<WorkerOut>) => {
      const m = ev.data;
      if (m.type === 'step') {
        if (m.step === 'notes') mark(m.share === 1 ? 'chords' : 'notes', 'run', m.share === 1 ? undefined : m.share);
        else if (m.step === 'chords') mark('structure', 'run');
        else if (m.step === 'structure') mark('sheet', 'run');
      } else if (m.type === 'done') {
        stop();
        paintResult(m.result, title);
      } else {
        stop();
        paintPick(`Analyse fehlgeschlagen: ${m.message}`);
      }
    };
    worker.onerror = () => {
      stop();
      if (!closed) paintPick('Analyse fehlgeschlagen. Bitte noch einmal versuchen.');
    };
    const msg: WorkerIn = { type: 'start', pcm, base: new URL('./', document.baseURI).href };
    // Uebertragen statt kopieren: danach liegt das Audio nur noch im Worker.
    worker.postMessage(msg, [pcm.buffer]);
  }

  /* ------------------------------------------ 3. Pruefen und speichern */

  function paintResult(a: LeadSheetAnalysis, suggestedTitle: string): void {
    const titleIn = h('input', { class: 'input', attrs: { type: 'text', value: suggestedTitle, placeholder: 'Titel', 'aria-label': 'Titel' } });
    const artistIn = h('input', { class: 'input', attrs: { type: 'text', placeholder: 'Interpret (optional)', 'aria-label': 'Interpret' } });
    const warnBox = h('div');
    const sectionsBox = h('div', { class: 'analysis__sections' });

    const chip = (label: string, value: string, c: number) =>
      h('span', { class: `analysis__chip analysis--${level(c)}`, attrs: { title: `${label}: ${value} · ${pct(c)} sicher` } }, h('small', { text: label }), h('strong', { text: value }));

    function paintWarnings(): void {
      a.warnings = validate(a);
      const top = a.warnings.filter((w) => w.code !== 'chord-unsure');
      const unsure = a.warnings.filter((w) => w.code === 'chord-unsure').length;
      warnBox.replaceChildren(
        ...top.map((w) => h('p', { class: 'analysis__warn', text: w.message })),
        ...(unsure ? [h('p', { class: 'hint', text: `${unsure} ${unsure === 1 ? 'Stelle ist' : 'Stellen sind'} markiert – antippen zum Ändern.` })] : []),
      );
    }

    function paintSections(): void {
      sectionsBox.replaceChildren(
        ...a.sections.map((s) =>
          h(
            'section',
            { class: 'analysis__section' },
            h(
              'div',
              { class: 'analysis__section-head' },
              h('button', {
                class: 'analysis__label',
                text: s.label,
                attrs: { type: 'button', title: 'Abschnitt umbenennen' },
                on: {
                  click: async () => {
                    const v = await askText({ title: 'Abschnitt', label: 'Name', value: s.label, confirm: 'Übernehmen' });
                    if (v) {
                      s.label = cleanLabel(v);
                      paintSections();
                    }
                  },
                },
              }),
              h('button', {
                class: 'analysis__repeat',
                text: s.repeat > 1 ? `${s.repeat}×` : '1×',
                attrs: { type: 'button', title: 'Wiederholungen ändern' },
                on: {
                  click: async () => {
                    const v = await askText({ title: 'Wiederholungen', label: 'Wie oft?', value: String(s.repeat), confirm: 'Übernehmen', inputMode: 'numeric' });
                    const n = Number(v);
                    if (Number.isInteger(n) && n >= 1 && n <= 64) {
                      s.repeat = n;
                      paintSections();
                    }
                  },
                },
              }),
            ),
            h(
              'div',
              { class: 'analysis__bars' },
              ...s.bars.map((bar, bi) =>
                h(
                  'div',
                  { class: 'analysis__bar' },
                  ...bar.chords
                    .filter((c) => c.symbol !== 'N')
                    .map((c) =>
                      h('button', {
                        class: `analysis__chord analysis--${level(c.confidence)}`,
                        text: c.symbol,
                        attrs: { type: 'button', title: `${c.symbol} · ${pct(c.confidence)}`, 'aria-label': `${s.label}, Takt ${bi + 1}: ${c.symbol}, ${pct(c.confidence)} sicher` },
                        on: {
                          click: async () => {
                            const v = await askText({ title: `Takt ${bi + 1}`, label: `Akkord (erkannt: ${c.symbol} · ${pct(c.confidence)}) – leer lassen zum Entfernen`, value: c.symbol, confirm: 'Übernehmen', allowEmpty: true });
                            if (v === null) return;
                            const clean = cleanChord(v);
                            if (clean) {
                              c.symbol = clean;
                              c.confidence = 1; // vom Menschen bestaetigt
                            } else bar.chords.splice(bar.chords.indexOf(c), 1);
                            paintSections();
                            paintWarnings();
                          },
                        },
                      }),
                    ),
                ),
              ),
            ),
          ),
        ),
      );
    }

    async function save(): Promise<void> {
      a.title = titleIn.value.trim().slice(0, 200);
      a.artist = artistIn.value.trim().slice(0, 200);
      const song = await saveSong(
        emptySong(projectId, {
          title: a.title,
          artist: a.artist,
          key: keyText(a),
          tempo: Math.round(a.tempo.bpm),
          timeSignature: `${a.timeSignature.beats}/${a.timeSignature.unit}`,
          body: toSheetText(a),
          analysis: a,
        }),
      );
      close();
      toast('Song angelegt – im Editor kannst du alles weiter anpassen.');
      onSaved(song);
    }

    headRight.replaceChildren(
      h('button', { class: 'btn btn--small', text: 'Verwerfen', attrs: { type: 'button' }, on: { click: close } }),
      h('button', { class: 'btn btn--primary btn--small', text: 'Als Song speichern', attrs: { type: 'button' }, on: { click: () => void save() } }),
    );
    body.replaceChildren(
      h('div', { class: 'analysis__meta' }, titleIn, artistIn),
      h(
        'div',
        { class: 'analysis__chips' },
        chip('Tonart', keyText(a), a.key.confidence),
        chip('Tempo', `${Math.round(a.tempo.bpm)} BPM`, a.tempo.confidence),
        chip('Takt', `${a.timeSignature.beats}/${a.timeSignature.unit}`, a.timeSignature.confidence),
      ),
      warnBox,
      sectionsBox,
      h('p', { class: 'hint', text: 'Abschnittsnamen sind Vorschläge. Tippe auf einen Akkord, einen Namen oder die Wiederholung, um ihn zu ändern.' }),
    );
    paintWarnings();
    paintSections();
  }

  const modal = h(
    'div',
    { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Song analysieren' } },
    h(
      'div',
      { class: 'modal__panel analysis' },
      h('div', { class: 'modal__head' }, h('h2', { class: 'modal__title', text: 'Song analysieren' }), headRight),
      body,
    ),
  );
  paintPick();
  document.addEventListener('keydown', onKey);
  document.body.append(modal);
}
